import React, { useEffect, useState, useRef } from "react";

// Import reactstrap components
import {
    Button,
    Card,
    CardHeader,
    CardBody,
    Form,
    Input,
    Container,
    Row,
    Col,
    FormGroup,
    Modal,
    Label,
} from "reactstrap";


// Core components
import SimpleHeader from "components/Headers/SimpleHeader";
import RegisterTable from "components/Tables/RegisterTable";
import RolePermissionsPicker from "components/Inputs/RolePermissionsPicker";
import { ALL_PERMISSIONS } from "constants/permissionLabels";

// Import API services
import {
    getAllUserRoles,
    addNewUserRole,
    updateUserRole,
    getPermissionsCatalog,
} from "services/admin";

// Constants
const emptyRoleForm = { name: '', permissions: [] };
const rolesTableMapping = {
    name: { tableHeading: 'Nombre' },
    permissionsSummary: { tableHeading: 'Permisos' },
}

// Utility function to format permissions summary
const formatPermissionsSummary = (permissions) => {
    if (!permissions || permissions.length === 0) {
        return 'Sin permisos';
    }
    if (permissions.includes(ALL_PERMISSIONS)) {
        return 'Acceso total';
    }
    return `${permissions.length} permiso(s)`;
};

// Main component
const UserRolesSettings = () => {
    // Declare references
    const addNewRef = useRef(null);
    // Declare states
    const [roles, setRoles] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [newRole, setNewRole] = useState(emptyRoleForm);
    const [columnKeys, setColumnKeys] = useState([]);
    // Edit Record Modal states
    const [editingRole, setEditingRole] = useState(null);
    const [updatedData, setUpdatedData] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        // Fetch roles and permissions catalog from server at first render
        fetchRoles();
        fetchCatalog();
    }, []);

    // Utility functions
    const fetchCatalog = () => {
        getPermissionsCatalog()
            .then((data) => {
                if (data.success) {
                    setCatalog(data.features);
                }
            });
    };

    const fetchRoles = () => {
        getAllUserRoles()
            .then((data) => {
                if (data.success) {
                    const displayRoles = data.roles.map((role) => ({
                        ...role,
                        permissionsSummary: formatPermissionsSummary(role.permissions),
                    }));
                    setRoles(displayRoles);
                    // Get column keys
                    let colKeys = data.table.column_keys.filter((k) => k !== 'id' && k !== 'permissions');
                    if (!colKeys.includes('permissionsSummary')) {
                        colKeys.push('permissionsSummary');
                    }
                    setColumnKeys(colKeys);
                }
            });
    };

    // Handlers
    const handleCreateInputsChange = (e) => {
        // Update newRole state
        setNewRole({ ...newRole, [e.target.name]: e.target.value });
    };

    const handleCreatePermissionsChange = (permissions) => {
        setNewRole({ ...newRole, permissions });
    };

    const handleClickAddNew = (e) => {
        e.preventDefault();
        addNewRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    const handleSubmitNew = (e) => {
        e.preventDefault();
        if (!newRole.name || newRole.permissions.length === 0) {
            alert('Ingrese nombre y al menos un permiso.');
            return;
        }
        setSaving(true);
        // Add new Role to server
        addNewUserRole(newRole)
            .then((data) => {
                if (data.success) {
                    setNewRole(emptyRoleForm);
                    // Fetch roles from server to update table
                    fetchRoles();
                } else {
                    alert(data.message || 'Ocurrió un error.');
                }
            })
            .catch(() => alert('Ocurrió un error.'))
            .finally(() => setSaving(false));
    };

    const handleOpenModalEdit = (e, roleId) => {
        e.preventDefault();
        const role = roles.find((r) => r.id === roleId);
        if (!role) {
            console.error('Role not found');
            return;
        }
        // Clear updatedData
        setUpdatedData({});
        setEditingRole(role);
    };

    const closeEditModal = () => {
        setEditingRole(null);
        setUpdatedData({});
    };

    const handleEditInputsChange = (e) => {
        const { name, value } = e.target;
        setUpdatedData({ ...updatedData, [name]: value });
    };

    const handleEditPermissionsChange = (permissions) => {
        setUpdatedData({ ...updatedData, permissions });
    };

    const handleUpdateRecord = () => {
        if (Object.keys(updatedData).length === 0) {
            closeEditModal();
            return;
        }
        if (updatedData.hasOwnProperty('name') && !updatedData.name) {
            alert('Ingrese nombre y al menos un permiso.');
            return;
        }
        if (updatedData.hasOwnProperty('permissions') && updatedData.permissions.length === 0) {
            alert('Ingrese nombre y al menos un permiso.');
            return;
        }
        setSaving(true);
        updateUserRole(editingRole.id, updatedData)
            .then((data) => {
                if (data.success) {
                    closeEditModal();
                    fetchRoles();
                } else {
                    alert(data.message || 'Ocurrió un error.');
                }
            })
            .catch(() => alert('Ocurrió un error.'))
            .finally(() => setSaving(false));
    };

    // Customization variables
    const headerButtons = [
        { className: 'btn-neutral', color: 'default', content: '+ Nuevo', handler: handleClickAddNew },
    ];

    // RegisterTable customization variables
    const inlineActions = [
        { text: 'Editar', handler: handleOpenModalEdit, property: 'id', icon: 'fas fa-edit' },
    ];

    // Render component
    return (
        <>
            <SimpleHeader
                name="Roles"
                parentName="Bases de datos"
                buttons={headerButtons}
            />
            <Container className="mt--6" fluid>
                {/* Render table to display all roles */}
                <RegisterTable
                    columnKeys={columnKeys}
                    records={roles}
                    tableMapping={rolesTableMapping}
                    tableTitle="Registro de Roles"
                    inlineActions={inlineActions}
                />
                {/* Render form for adding new roles */}
                <Card className="mb-4" id="add-new">
                    <CardHeader>
                        <h3 ref={addNewRef}>Agregar nuevo rol</h3>
                    </CardHeader>
                    <CardBody>
                        <RoleForm
                            formType="create"
                            formData={newRole}
                            onChange={handleCreateInputsChange}
                            onPermissionsChange={handleCreatePermissionsChange}
                            onSubmit={handleSubmitNew}
                            catalog={catalog}
                            saving={saving}
                        />
                    </CardBody>
                </Card>
            </Container>

            {/* Modal for editing an existing role */}
            <Modal
                className="modal-dialog-centered"
                isOpen={editingRole != null}
                toggle={closeEditModal}
                size="lg"
            >
                <div className="modal-header">
                    <h3 className="modal-title">Editar rol</h3>
                    <button
                        aria-label="Close"
                        className="close"
                        data-dismiss="modal"
                        type="button"
                        onClick={closeEditModal}
                    >
                        <span aria-hidden={true}>×</span>
                    </button>
                </div>
                <div className="modal-body">
                    <p>
                        Puedes modificar el siguiente <strong>Rol</strong>:
                    </p>
                    <RoleForm
                        formType="edit"
                        defaultData={editingRole}
                        updatedData={updatedData}
                        onChange={handleEditInputsChange}
                        onPermissionsChange={handleEditPermissionsChange}
                        catalog={catalog}
                    />
                </div>
                <div className="modal-footer">
                    <Button
                        color="info"
                        type="button"
                        onClick={handleUpdateRecord}
                        disabled={saving}
                    >
                        {saving ? 'Guardando…' : 'Guardar'}
                    </Button>
                    <Button
                        className="ml-auto"
                        color="link"
                        type="button"
                        onClick={closeEditModal}
                        disabled={saving}
                    >
                        Cancelar
                    </Button>
                </div>
            </Modal>
        </>
    );
};

/**
 * Shared create/edit role fields (name + permissions picker).
 * Create: controlled formData. 
 * Edit: defaultData + updatedData (only changed fields).
 */
const RoleForm = ({
    formType,
    formData,
    defaultData,
    updatedData = {},
    onChange: handleChange,
    onPermissionsChange: handlePermissionsChange,
    onSubmit: handleSubmit,
    catalog,
    saving = false,
}) => {
    const nameValue = formType === 'create'
        ? (formData?.name || '')
        : (updatedData.hasOwnProperty('name') ? updatedData.name : (defaultData?.name || ''));

    const permissionsValue = formType === 'create'
        ? (formData?.permissions || [])
        : (updatedData.hasOwnProperty('permissions')
            ? updatedData.permissions
            : (defaultData?.permissions || []));

    return (
        <Form
            onSubmit={(e) => {
                e.preventDefault();
                if (handleSubmit) {
                    handleSubmit(e);
                }
            }}
        >
            <Row>
                <Col sm="6">
                    <FormGroup>
                        <Label
                            className="form-control-label"
                            for={`name-${formType}`}
                        >
                            Nombre de rol
                        </Label>
                        <Input
                            id={`name-${formType}`}
                            placeholder="Ejemplo: Analista, Supervisor, etc."
                            type="text"
                            name="name"
                            value={nameValue}
                            onChange={handleChange}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col>
                    <FormGroup>
                        <h3>Permisos del rol</h3>
                    </FormGroup>
                </Col>
            </Row>
            <RolePermissionsPicker
                formType={formType}
                catalog={catalog}
                value={permissionsValue}
                onChange={handlePermissionsChange}
            />
            {formType === 'create' ? (
                <div className="text-left">
                    <Button
                        color="primary"
                        type="submit"
                        disabled={saving}
                    >
                        Registrar
                    </Button>
                </div>
            ) : null}
        </Form>
    );
};

export default UserRolesSettings;
